import React from 'react';
import {
  Checkbox,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
//
import { uploadFile } from '@utils';
import { AppUpload } from '@components';
import { useField } from 'formik';
import { useAuthContext } from '../auth';
import ObTooltip from './ObTooltip';

const types = [
  'Photo ID',
  'Utility Bill',
  'Article of Incorporation',
  'Vehicle Registration Certificate',
];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const DocUpload: React.FC = () => {
  // Get OB data from AuthContext
  const { obData } = useAuthContext();

  const [id, setId] = React.useState<File | any>(null);
  const [driving, setDriving] = React.useState<File | any>(null);
  const [utility, setUtility] = React.useState<File | any>(null);
  const [vehicle, setVehicle] = React.useState<File | any>(null);
  const [documents, setDocuments] = React.useState({
    idCard: obData?.documents?.idCard || '',
    driving: obData?.documents?.driving || '',
    utility: obData?.documents?.utility || '',
    vehicle: obData?.documents?.vehicle || '',
  });
  const [docTypes, setDocTypes] = React.useState([
    'Photo ID',
    'Utility Bill',
    'Article of Incorporation',
  ] as string[]);

  // console.log(obData);

  const [field, meta] = useField('documents');
  const { error, value } = meta;
  const { onChange } = field;

  React.useEffect(() => {
    onChange({ target: { name: 'documents', value: documents } });
  }, [documents, value]);

  const handleDropId = React.useCallback(
    (acceptedFiles: File[]) => {
      const newFile = acceptedFiles[0];
      if (newFile) {
        setId(
          Object.assign(newFile, {
            preview: URL.createObjectURL(newFile),
          })
        );

        const { url, preview } = uploadFile(newFile, obData?.id, 'id');
        if (url && preview) {
          setDocuments({ ...documents, idCard: url });
        }
      }
    },
    [documents]
  );

  const handleDropDriving = React.useCallback(
    (acceptedFiles: File[]) => {
      const newFile = acceptedFiles[0];
      if (newFile) {
        setDriving(
          Object.assign(newFile, {
            preview: URL.createObjectURL(newFile),
          })
        );

        const { url, preview } = uploadFile(newFile, obData?.id, 'driver');
        if (url && preview) {
          setDocuments({ ...documents, driving: url });
        }
      }
    },
    [documents]
  );

  const handleDropUtility = React.useCallback(
    (acceptedFiles: File[]) => {
      const newFile = acceptedFiles[0];
      if (newFile) {
        setUtility(
          Object.assign(newFile, {
            preview: URL.createObjectURL(newFile),
          })
        );

        const { url, preview } = uploadFile(newFile, obData?.id, 'bill');
        if (url && preview) {
          setDocuments({ ...documents, utility: url });
        }
      }
    },
    [documents]
  );

  const handleDropVehicle = React.useCallback(
    (acceptedFiles: File[]) => {
      const newFile = acceptedFiles[0];
      if (newFile) {
        setVehicle(
          Object.assign(newFile, {
            preview: URL.createObjectURL(newFile),
          })
        );

        const { url, preview } = uploadFile(newFile, obData?.id, 'car');
        if (url && preview) {
          setDocuments({ ...documents, vehicle: url });
        }
      }
    },
    [documents]
  );

  const handleChange = (event: SelectChangeEvent<typeof types>) => {
    const {
      target: { value },
    } = event;
    setDocTypes(typeof value === 'string' ? value.split(',') : value);
  };

  return (
    <>
      <Typography variant="h4" component="h2" gutterBottom>
        Upload your documents
      </Typography>
      <Typography variant="body1" gutterBottom color="text.secondary">
        {import.meta.env.VITE_SITE} requires some documents in order to verify
        your identity. By uploading these documents, you ensure that no one else
        can log in to your account.
      </Typography>
      <ObTooltip
        title={
          <>
            <Typography variant="subtitle2" gutterBottom>
              <strong>Photo ID:</strong>
            </Typography>
            <Typography variant="body2">
              <strong>Example: </strong>Drivers license
            </Typography>
            <Typography variant="body2">
              <strong>File types:</strong> jpg/jpeg, png, pdf
            </Typography>
            <Typography variant="body2">
              <strong>Size:</strong> less than 5mb
            </Typography>
            <Typography variant="subtitle2" gutterBottom mt={3}>
              <strong>Utility Bill:</strong>
            </Typography>
            <Typography variant="body2">
              <strong>Example: </strong>Hydro bill, Gas bill
            </Typography>
            <Typography variant="body2">
              <strong>File types:</strong> jpg/jpeg, png, pdf
            </Typography>
            <Typography variant="body2">
              <strong>Size:</strong> less than 5mb
            </Typography>
            <Typography variant="subtitle2" gutterBottom mt={3}>
              <strong>Article of Incorporation:</strong>
            </Typography>
            <Typography variant="body2">
              <strong>Example: </strong>Business Registration
            </Typography>
            <Typography variant="body2">
              <strong>File types:</strong> jpg/jpeg, png, pdf
            </Typography>
            <Typography variant="body2">
              <strong>Size:</strong> less than 5mb
            </Typography>
          </>
        }
      >
        <Grid container mt={1} spacing={2}>
          {docTypes.includes('Photo ID') && (
            <Grid item xs={12} md={6}>
              <Typography variant="body2" gutterBottom>
                Photo ID
              </Typography>
              <AppUpload
                file={id}
                onDrop={handleDropId}
                onDelete={() => setId(null)}
                accept={{
                  'image/png': ['.png', '.jpg', '.jpeg'],
                  'application/pdf': ['.pdf'],
                }}
              />
            </Grid>
          )}

          {docTypes.includes('Utility Bill') && (
            <Grid item xs={12} md={6}>
              <Typography variant="body2" gutterBottom>
                Utility Bill
              </Typography>
              <AppUpload
                file={driving}
                onDrop={handleDropDriving}
                onDelete={() => setDriving(null)}
                accept={{
                  'image/png': ['.png', '.jpg', '.jpeg'],
                  'application/pdf': ['.pdf'],
                }}
              />
            </Grid>
          )}

          {docTypes.includes('Article of Incorporation') && (
            <Grid item xs={12} md={6}>
              <Typography variant="body2" gutterBottom>
                Article of Incorporation
              </Typography>
              <AppUpload
                file={utility}
                onDrop={handleDropUtility}
                onDelete={() => setUtility(null)}
                accept={{
                  'image/png': ['.png', '.jpg', '.jpeg'],
                  'application/pdf': ['.pdf'],
                }}
              />
            </Grid>
          )}

          {docTypes.includes('Vehicle Registration Certificate') && (
            <Grid item xs={12} md={6}>
              <Typography variant="body2" gutterBottom>
                Vehicle Registration Certificate
              </Typography>
              <AppUpload
                file={vehicle}
                onDrop={handleDropVehicle}
                onDelete={() => setVehicle(null)}
                accept={{
                  'image/png': ['.png', '.jpg', '.jpeg'],
                  'application/pdf': ['.pdf'],
                }}
              />
            </Grid>
          )}
        </Grid>
      </ObTooltip>

      {/* <FormControl size="small" sx={{ mt: 4, width: '100%' }}>
        <InputLabel id="docTypes">Other Documents</InputLabel>
        <Select
          labelId="docTypes"
          id="docTypes-checkbox"
          multiple
          value={docTypes}
          onChange={handleChange}
          input={<OutlinedInput label="Other Documents" />}
          renderValue={(selected) => selected.join(', ')}
          MenuProps={MenuProps}
        >
          {types.map((name) => (
            <MenuItem key={name} value={name}>
              <Checkbox size="small" checked={docTypes.indexOf(name) > -1} />
              <ListItemText primary={name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl> */}
    </>
  );
};

export default DocUpload;
